import IWebsocketMessageHandler from '@@types/IWebsocketMessageHandler';

import GA from '~/services/GA/GA';
import logging from '~/stores/providers/logging';
import { get, uniqueId } from 'lodash';
import { action, observable } from 'mobx';
import isNil from 'ramda/es/isNil';

const WEBSOCKET_PING_INTERVAL = 1000 * 10;

class WebsocketConnector {
  constructor(private messageHandler: IWebsocketMessageHandler) {}

  @observable
  openedSocketId: string;

  socketId: string;
  connectPromise: Promise<void> | null = null;
  socket: WebSocket | null = null;
  socketResolver: ((value?: any) => void) | null = null;
  socketRejector: ((reason?: any) => void) | null = null;

  pingIntervalId: number | null = null;
  pingCount = 0;
  stopRetry = false;
  sendBuffer = [];

  connect = (url: string) => {
    if (this.connectPromise !== null) {
      return this.connectPromise;
    }

    if (this.socket !== null) {
      this.socket.onopen = null;
      this.socket.onerror = null;
      this.socket.onclose = null;
      this.socket.onmessage = null;
    }

    this.socket = new WebSocket(url);
    this.setSocketId(uniqueId());
    this.socket.onopen = this.onOpen;
    this.socket.onerror = this.onError as unknown as (this: WebSocket, ev: Event) => any;
    this.socket.onclose = this.onClose;
    this.socket.onmessage = this.onMessage;
    this.connectPromise = new Promise((resolve, reject) => {
      this.socketResolver = resolve;
      this.socketRejector = reject;
      setTimeout(this.rejectConnection, 5000);
    });
    return this.connectPromise;
  };

  setSocketId(socketId: string | null) {
    this.socketId = socketId;
  }

  @action
  setOpenedSocketId(socketId: string | null) {
    this.openedSocketId = socketId;
  }

  private onOpen = () => {
    logging.log('socket opened');
    this.socketResolver?.();
    this.sendBuffer.forEach(this.send);
    this.sendBuffer = [];
    this.socketResolver = null;
    this.connectPromise = null;
    this.stopRetry = false;
    this.startPingInterval();
    this.messageHandler.onOpen?.();
    this.setOpenedSocketId(uniqueId());
  };

  private onMessage = ({ data }: MessageEvent) => {
    this.messageHandler.handleMessage(data);
  };

  private onError = (e: Error) => {
    logging.log('socket error', e);
    this.rejectConnection();
    this.messageHandler.onError?.(e, this.socketId);
  };

  private onClose = () => {
    logging.log('socket closed');
    this.rejectConnection();
    this.messageHandler.onClose?.(this.socketId);
    this.connectPromise = null;
    this.clearPingCount();
    this.pingIntervalId && window.clearInterval(this.pingIntervalId);
  };

  private rejectConnection = () => {
    if (this.socketRejector) {
      this.socketRejector();
      this.socketRejector = null;
      this.connectPromise = null;
    }
  };

  private startPingInterval = () => {
    this.pingIntervalId = window.setInterval(() => {
      if (this.pingCount > 2) {
        GA.writeCloseWebsocket('PING');
        this.close();
      }
      this.send({ type: 'ping' });
      this.pingCount += 1;
    }, WEBSOCKET_PING_INTERVAL);
  };

  close = (stopRetry = false) => {
    if (isNil(this.socket)) return;
    this.stopRetry = stopRetry;
    this.socket.close();
    logging.log('try socket close');
  };

  send = (data: { type: string; action?: string; content?: any }) => {
    if (this.isConnected()) {
      this.socket && this.socket.send(JSON.stringify(data));
    } else {
      this.sendBuffer.push(data as never);
    }
  };

  public hasSocket = () => {
    return this.socket !== null;
  };

  public isConnected = () => {
    return get(this.socket, 'readyState') === get(this.socket, 'OPEN', false);
  };

  clearPingCount = () => {
    this.pingCount = 0;
  };

  setStopRetry = (value) => {
    this.stopRetry = value;
  };

  public resetSocketId = () => {
    this.setSocketId(null);
  };
}

export default WebsocketConnector;
